// §30.6 (D-369, D-371) — Standings: today's daily boards, inside Connections.
//
// The wave-1 scoreboard was buried with a clause — "revisit if the scores turn out to be social" —
// and the daily deal is exactly what makes them social: one board a day, the same for everybody,
// and the only people on it are the ones you are already connected to (§26.9). So it lands *inside*
// the roster app rather than as a fourteenth icon, which is the burial's own reasoning honoured
// rather than reversed.
//
// The ranking is not this file's. `DAILY_RANKING` and `beatsDaily` live in `shared` and the server
// upserts by the same function, so a board cannot disagree with the row it stored.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DAILY_GAME_IDS,
  DAILY_RANKING,
  type Connection,
  type DailyScore,
  type HeadToHead,
  type SocialShellAccess,
} from '@hangar/shared';
import { COPY } from '@/copy';

/** The titles a board can be picked for, named where a person picks one. */
const GAME_LABELS: Readonly<Record<string, string>> = {
  freecell: 'FreeCell',
  pyramid: 'Pyramid',
  chess: 'Chess',
  backgammon: 'Backgammon',
  conquest: 'Conquest',
  intercept: 'Intercept',
};

/** `m:ss`, the same shape the games show. */
export function formatMs(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * What a row says, given the game's ranking.
 *
 * FreeCell is ranked on time, so time is what its rows lead with; Pyramid is ranked on
 * score. Showing both either way would make the column that decides the order the harder one to
 * find.
 */
export function rowSummary(entry: DailyScore): string {
  const ranking = DAILY_RANKING[entry.gameId as keyof typeof DAILY_RANKING] ?? 'score';
  const time = formatMs(entry.ms);
  if (!entry.won) return `${COPY.game.unfinished} · ${time}`;
  return ranking === 'score' ? `${entry.score} · ${time}` : time;
}

export interface StandingsProps {
  access?: SocialShellAccess;
  /** Injected in tests; `access.today()` otherwise. */
  day?: string;
  /** The accepted connections a head-to-head tally can be asked about (§26.9). */
  connections?: readonly Connection[];
}

export function Standings({ access, day, connections = [] }: StandingsProps) {
  const [gameId, setGameId] = useState<string>(DAILY_GAME_IDS[0]);
  const [rows, setRows] = useState<readonly DailyScore[]>([]);
  const [loaded, setLoaded] = useState(false);

  const today = day ?? access?.today();

  const refresh = useCallback(async () => {
    if (access === undefined || today === undefined) return;
    const scores = await access.dailyScores(gameId, today).catch(() => [] as DailyScore[]);
    setRows(scores);
    setLoaded(true);
  }, [access, gameId, today]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // §26.6 — the doorbell says something changed; this re-reads. A connection finishing today's
  // deal is a `presence` frame away, so the board keeps up without a poll of its own.
  useEffect(() => {
    if (access === undefined) return;
    return access.subscribe(() => {
      void refresh();
    });
  }, [access, refresh]);

  const me = access?.me() ?? null;
  const ranked = useMemo(() => rows.map((row, index) => ({ row, place: index + 1 })), [rows]);

  /**
   * §30.5 / §30.6 — the head-to-head half.
   *
   * Only accepted connections appear, which is §26.9's posture and the same rule the server
   * enforces: a tally against a stranger would be a way to learn that a stranger exists.
   */
  const accepted = useMemo(
    () => connections.filter((connection) => connection.state === 'accepted'),
    [connections],
  );
  const [records, setRecords] = useState<Readonly<Record<string, readonly HeadToHead[]>>>({});

  useEffect(() => {
    if (access === undefined || accepted.length === 0) return;
    let cancelled = false;
    void Promise.all(
      accepted.map(async (connection) => {
        const tally = await access.headToHead(connection.peer).catch(() => [] as HeadToHead[]);
        return [connection.peer, tally] as const;
      }),
    ).then((entries) => {
      if (!cancelled) setRecords(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [access, accepted]);

  const anyRecords = accepted.some((connection) => (records[connection.peer] ?? []).length > 0);

  return (
    <section className="conn-standings" data-testid="standings">
      <h2 className="conn-group-title">{COPY.game.standings}</h2>

      <div className="conn-standings-head">
        <h3 className="conn-standings-title" data-testid="standings-daily-title">
          {COPY.game.todaysDeals}
        </h3>
        <div className="conn-standings-picker" role="group" aria-label={COPY.game.todaysDeals}>
          {DAILY_GAME_IDS.map((id) => (
            <button
              type="button"
              key={id}
              data-testid={`standings-pick-${id}`}
              aria-pressed={gameId === id}
              onClick={() => setGameId(id)}
            >
              {GAME_LABELS[id] ?? id}
            </button>
          ))}
        </div>
      </div>

      {loaded && ranked.length === 0 ? (
        <p className="conn-standings-empty" data-testid="standings-empty">
          {COPY.game.noPlaysToday}
        </p>
      ) : (
        <ol className="conn-standings-list" data-testid="standings-list">
          {ranked.map(({ row, place }) => (
            <li
              className="conn-standings-row"
              key={row.userRef}
              data-testid={`standings-row-${row.userRef}`}
              data-self={String(row.userRef === me)}
            >
              <span className="conn-standings-place">{place}</span>
              <span className="conn-standings-name">{row.displayName}</span>
              <span className="conn-standings-score">{rowSummary(row)}</span>
            </li>
          ))}
        </ol>
      )}

      <h3 className="conn-standings-title" data-testid="standings-h2h-title">
        {COPY.game.headToHead}
      </h3>
      {anyRecords ? (
        <ul className="conn-standings-list" data-testid="standings-h2h">
          {accepted.map((connection) =>
            (records[connection.peer] ?? []).map((record) => (
              <li
                className="conn-standings-row"
                key={`${connection.peer}:${record.gameId}`}
                data-testid={`standings-h2h-${connection.peer}-${record.gameId}`}
              >
                <span className="conn-standings-place">{GAME_LABELS[record.gameId] ?? record.gameId}</span>
                <span className="conn-standings-name">{connection.displayName}</span>
                <span className="conn-standings-score">
                  {record.won}–{record.lost}
                  {record.drawn > 0 ? `–${record.drawn}` : ''}
                </span>
              </li>
            )),
          )}
        </ul>
      ) : (
        <p className="conn-standings-empty" data-testid="standings-h2h-empty">
          {COPY.game.noFinishedGames}
        </p>
      )}
    </section>
  );
}
